import { useCallback, useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';
import {
  cameraTransform, fitBounds, lerpCamera, panBy, zoomAt, zoomLevel,
  type Bounds, type Camera, type Viewport, type ZoomLevel,
} from '../camera';

export interface PanZoom {
  level: ZoomLevel;
  active: boolean;
  hint: boolean;
  getCamera(): Camera;
  flyTo(target: Camera): void;
  /** Ride along with something moving in the world: shift the camera (and any flight's
   *  start and end) by a world-unit delta, so it stays put on screen. */
  follow(dx: number, dy: number): void;
  zoomBy(factor: number): void;
  fit(): void;
}

const DRAG_THRESHOLD = 4;
const FLY_MS = 450;
const HINT_MS = 1600;
const KEY_ZOOM = 1.25;
const KEY_PAN = 60;
/** How long the camera must stay still after a gesture or flight before it counts as settled. */
export const SETTLE_MS = 300;
const ease = (k: number) => (k < 0.5 ? 2 * k * k : 1 - Math.pow(-2 * k + 2, 2) / 2);

/**
 * Pan/zoom for an SVG world group. The camera lives in a ref and is written
 * straight to the group's transform (no React render per wheel tick or drag
 * move); React state changes only when the zoom level crosses a threshold,
 * the canvas becomes active, or the hint toggles.
 */
export function usePanZoom(opts: {
  containerRef: RefObject<HTMLElement | null>;
  worldRef: RefObject<SVGGElement | null>;
  viewport: Viewport;
  bounds: Bounds;
  initial?: Camera | null;
  reducedMotion: boolean;
  /**
   * The camera came to rest after a user gesture (wheel, drag, pinch, keys) or a flight,
   * debounced by SETTLE_MS. Never per frame, and not for `follow` or a viewport refit.
   */
  onSettle?: () => void;
}): PanZoom {
  const { containerRef, worldRef, viewport, bounds, initial, reducedMotion } = opts;
  const onSettleRef = useRef(opts.onSettle);
  onSettleRef.current = opts.onSettle;
  const settleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const moved = useCallback(() => {
    if (settleTimer.current) clearTimeout(settleTimer.current);
    settleTimer.current = setTimeout(() => {
      settleTimer.current = null;
      onSettleRef.current?.();
    }, SETTLE_MS);
  }, []);
  // Leaving with a move still settling (select, then "Open project" at once): report it now
  // rather than drop it, so Back comes back to where the camera was heading.
  useEffect(() => () => {
    if (!settleTimer.current) return;
    clearTimeout(settleTimer.current);
    settleTimer.current = null;
    onSettleRef.current?.();
  }, []);
  const camRef = useRef<Camera>(initial ?? fitBounds(bounds, viewport));
  const vpRef = useRef(viewport);
  const interacted = useRef(Boolean(initial));
  const [level, setLevel] = useState<ZoomLevel>(zoomLevel(camRef.current.scale));
  const [active, setActive] = useState(false);
  const [hint, setHint] = useState(false);
  const flight = useRef(0);
  const hintTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const boundsRef = useRef(bounds);
  boundsRef.current = bounds;
  const isActiveRef = useRef(false);

  const apply = useCallback(() => {
    const cam = camRef.current;
    const g = worldRef.current;
    if (g) {
      g.setAttribute('transform', cameraTransform(cam, vpRef.current));
      g.style.setProperty('--lk', String(1 / cam.scale));
    }
    setLevel(zoomLevel(cam.scale));
  }, [worldRef]);

  const set = useCallback((cam: Camera) => {
    camRef.current = cam;
    apply();
  }, [apply]);

  // Viewport changes: keep the centre; refit while the user hasn't touched anything.
  useLayoutEffect(() => {
    vpRef.current = viewport;
    if (!interacted.current) camRef.current = fitBounds(bounds, viewport);
    apply();
  }, [viewport, bounds, apply]);

  const stopFlight = useCallback(() => {
    cancelAnimationFrame(flight.current);
    flight.current = 0;
  }, []);

  /** The current flight's endpoints, so `follow` can carry them along. */
  const flightEnds = useRef<{ from: Camera; to: Camera } | null>(null);
  const flyTo = useCallback((target: Camera) => {
    interacted.current = true;
    stopFlight();
    if (reducedMotion || typeof requestAnimationFrame !== 'function') {
      set(target);
      return moved();
    }
    const ends = { from: camRef.current, to: target };
    flightEnds.current = ends;
    const start = performance.now();
    const step = (t: number) => {
      const k = Math.max(0, Math.min(1, (t - start) / FLY_MS));
      set(lerpCamera(ends.from, ends.to, ease(k)));
      flight.current = k < 1 ? requestAnimationFrame(step) : 0;
      if (k >= 1) moved();
    };
    flight.current = requestAnimationFrame(step);
  }, [reducedMotion, set, stopFlight, moved]);

  const follow = useCallback((dx: number, dy: number) => {
    if (!dx && !dy) return;
    const shift = (c: Camera): Camera => ({ ...c, x: c.x + dx, y: c.y + dy });
    if (flight.current && flightEnds.current) {
      flightEnds.current.from = shift(flightEnds.current.from);
      flightEnds.current.to = shift(flightEnds.current.to);
    }
    set(shift(camRef.current));
  }, [set]);

  const zoomBy = useCallback((factor: number) => {
    interacted.current = true;
    const vp = vpRef.current;
    flyTo(zoomAt(camRef.current, vp, factor, { x: vp.width / 2, y: vp.height / 2 }));
  }, [flyTo]);

  const fit = useCallback(() => flyTo(fitBounds(bounds, vpRef.current)), [bounds, flyTo]);

  const flyToRef = useRef(flyTo);
  flyToRef.current = flyTo;
  const setRef = useRef(set);
  setRef.current = set;
  const stopFlightRef = useRef(stopFlight);
  stopFlightRef.current = stopFlight;
  const movedRef = useRef(moved);
  movedRef.current = moved;

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const pointers = new Map<number, { x: number; y: number }>();
    let dragged = false;
    let startPt: { x: number; y: number } | null = null;
    let pinchDist = 0;
    let clearTimer: ReturnType<typeof setTimeout> | null = null;
    const local = (e: { clientX: number; clientY: number }) => {
      const r = el.getBoundingClientRect();
      return { x: e.clientX - r.left, y: e.clientY - r.top };
    };
    const showHint = () => {
      setHint(true);
      if (hintTimer.current) clearTimeout(hintTimer.current);
      hintTimer.current = setTimeout(() => setHint(false), HINT_MS);
    };
    const activate = () => {
      if (!isActiveRef.current) {
        isActiveRef.current = true;
        setActive(true);
      }
    };

    const onWheel = (e: WheelEvent) => {
      const pinchy = e.ctrlKey || e.metaKey;
      if (!pinchy && !isActiveRef.current) return showHint();
      e.preventDefault();
      interacted.current = true;
      stopFlightRef.current();
      const delta = e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY;
      // A trackpad pinch sends small fractional deltas; a mouse notch (line mode, or ~100 px)
      // must zoom gently (≈ ×1.28) whether or not ctrl is held, or three notches overshoot a planet.
      const notch = e.deltaMode === 1 || Math.abs(delta) >= 40;
      const factor = Math.exp(-delta * (pinchy && !notch ? 0.01 : 0.0025));
      setRef.current(zoomAt(camRef.current, vpRef.current, factor, local(e)));
      movedRef.current();
    };
    const onDown = (e: PointerEvent) => {
      activate();
      pointers.set(e.pointerId, local(e));
      startPt = local(e);
      dragged = false;
      if (pointers.size === 2) {
        const [a, b] = [...pointers.values()];
        pinchDist = Math.hypot(a.x - b.x, a.y - b.y);
      }
    };
    const onMove = (e: PointerEvent) => {
      const prev = pointers.get(e.pointerId);
      if (!prev) return;
      const p = local(e);
      if (!dragged && startPt && Math.hypot(p.x - startPt.x, p.y - startPt.y) > DRAG_THRESHOLD) {
        dragged = true;
        interacted.current = true;
        stopFlightRef.current();
        el.setPointerCapture?.(e.pointerId);
      }
      pointers.set(e.pointerId, p);
      if (!dragged) return;
      if (pointers.size === 1) {
        setRef.current(panBy(camRef.current, p.x - prev.x, p.y - prev.y));
      } else if (pointers.size === 2) {
        const [a, b] = [...pointers.values()];
        const dist = Math.hypot(a.x - b.x, a.y - b.y);
        const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
        if (pinchDist > 0) setRef.current(zoomAt(camRef.current, vpRef.current, dist / pinchDist, mid));
        pinchDist = dist;
      }
      movedRef.current();
    };
    const onUp = (e: PointerEvent) => {
      pointers.delete(e.pointerId);
      if (pointers.size < 2) pinchDist = 0;
      // Only the click that ends this drag is suppressed, not a later keyboard click.
      if (dragged) {
        if (clearTimer) clearTimeout(clearTimer);
        clearTimer = setTimeout(() => { dragged = false; }, 0);
      }
    };
    // A drag must not also click whatever was under the pointer.
    const onClickCapture = (e: MouseEvent) => {
      if (dragged) {
        e.stopPropagation();
        e.preventDefault();
        dragged = false;
      }
    };
    const onDblClick = (e: MouseEvent) => {
      if (e.defaultPrevented) return;
      flyToRef.current(zoomAt(camRef.current, vpRef.current, 2, local(e)));
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return; // browser zoom shortcuts keep working
      if (e.target !== el) return; // planets and buttons keep their own keys
      const vp = vpRef.current;
      const c = { x: vp.width / 2, y: vp.height / 2 };
      const map: Record<string, () => Camera> = {
        '+': () => zoomAt(camRef.current, vp, KEY_ZOOM, c),
        '=': () => zoomAt(camRef.current, vp, KEY_ZOOM, c),
        '-': () => zoomAt(camRef.current, vp, 1 / KEY_ZOOM, c),
        '\u2212': () => zoomAt(camRef.current, vp, 1 / KEY_ZOOM, c),
        '0': () => fitBounds(boundsRef.current, vp),
        ArrowLeft: () => panBy(camRef.current, KEY_PAN, 0),
        ArrowRight: () => panBy(camRef.current, -KEY_PAN, 0),
        ArrowUp: () => panBy(camRef.current, 0, KEY_PAN),
        ArrowDown: () => panBy(camRef.current, 0, -KEY_PAN),
      };
      const next = map[e.key];
      if (!next) return;
      e.preventDefault();
      interacted.current = true;
      stopFlightRef.current();
      setRef.current(next());
      movedRef.current();
    };
    const onFocusIn = () => activate();
    const onDocDown = (e: PointerEvent) => {
      if (!el.contains(e.target as Node) && isActiveRef.current) {
        isActiveRef.current = false;
        setActive(false);
      }
    };

    el.addEventListener('wheel', onWheel, { passive: false });
    el.addEventListener('pointerdown', onDown);
    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerup', onUp);
    el.addEventListener('pointercancel', onUp);
    el.addEventListener('click', onClickCapture, true);
    el.addEventListener('dblclick', onDblClick);
    el.addEventListener('keydown', onKey);
    el.addEventListener('focusin', onFocusIn);
    document.addEventListener('pointerdown', onDocDown);
    return () => {
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('pointerdown', onDown);
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerup', onUp);
      el.removeEventListener('pointercancel', onUp);
      el.removeEventListener('click', onClickCapture, true);
      el.removeEventListener('dblclick', onDblClick);
      el.removeEventListener('keydown', onKey);
      el.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('pointerdown', onDocDown);
      if (hintTimer.current) clearTimeout(hintTimer.current);
      if (clearTimer) clearTimeout(clearTimer);
      cancelAnimationFrame(flight.current);
    };
  }, [containerRef]);

  return {
    level, active, hint, getCamera: () => camRef.current, flyTo, follow, zoomBy, fit,
  };
}
